import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMButton } from "../../../utils/PMButton";
import { PMField } from "../../../utils/PMField";
import { RadioGroup, FormControlLabel, Radio, FormLabel, Checkbox, TextField } from "@mui/material";
import { CheckCircleIcon, TruckIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

export const BulkEditConsumptionDialog = ({ 
  open, 
  onClose, 
  items, 
  onSave,
  report,
  onSummary 
}) => {
  const [selectedItems, setSelectedItems] = useState([]);

  // Filter out manual products (items without product_id) and only show picked, non-invoiced items
  const pickedItems = items.filter((item) => item.picked && !item.invoiced && item.product_id);

  // Create initial values with quantity per item
  const getInitialValues = () => {
    const itemQuantities = {};
    pickedItems.forEach((item) => {
      // Default to max quantity (picked quantity) for each item
      itemQuantities[`quantity_${item.id}`] = item.quantity || 0;
    });
    
    return {
      consumption_status: "",
      apply_to_all: false,
      ...itemQuantities,
    };
  };

  const initialValues = getInitialValues();

  const validationSchema = Yup.object({
    consumption_status: Yup.string().required("Status må velges"),
    // Dynamic validation for each item's quantity
    ...pickedItems.reduce((acc, item) => {
      acc[`quantity_${item.id}`] = Yup.number()
        .when("consumption_status", {
          is: (val) => val === "used_on_job" || val === "taken_in_vehicle",
          then: (schema) => schema
            .required("Antall er påkrevd")
            .min(0, "Antall kan ikke være negativt")
            .max(item.quantity, `Antall kan ikke være større enn ${item.quantity} (plukket antall)`),
          otherwise: (schema) => schema.nullable(),
        });
      return acc;
    }, {}),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (selectedItems.length === 0 && !values.apply_to_all) {
        toast.error("Velg minst ett produkt å oppdatere");
        setSubmitting(false);
        return;
      }

      const itemsToUpdate = values.apply_to_all ? pickedItems : pickedItems.filter(item => selectedItems.includes(item.id));

      // Update all items with individual quantities
      const updatePromises = itemsToUpdate.map(async (item) => {
        // Get quantity for this specific item
        const quantityUsed = parseFloat(values[`quantity_${item.id}`]) || 0;
        
        // Validate quantity for this item
        if (quantityUsed > item.quantity) {
          toast.error(`${item.product?.displayTitle || item.title}: Antall kan ikke være større enn ${item.quantity} (plukket antall)`);
          throw new Error(`Invalid quantity for item ${item.id}`);
        }

        const quantityNotUsed = quantityUsed === 0 
          ? item.quantity 
          : (item.quantity - quantityUsed);

        const updatedItem = {
          ...item,
          taken_in_vehicle: values.consumption_status === "taken_in_vehicle",
          quantity_used: values.consumption_status ? quantityUsed : null,
          quantity_not_used: quantityNotUsed > 0 ? quantityNotUsed : null,
          returned_to_storage: false,
          left_in_vehicle: false,
          storage_id: null,
        };

        // Reset other statuses
        if (values.consumption_status !== "taken_in_vehicle") {
          updatedItem.taken_in_vehicle = false;
        }

        return await onSave(updatedItem);
      });

      await Promise.all(updatePromises);
      
      // Calculate summary for updated items
      const summary = {
        used: [],
        needsHandling: [],
      };

      itemsToUpdate.forEach((item) => {
        const productName = item.product?.displayTitle || item.product?.title || item.title || `Produkt ${item.product_id || "Ukjent"}`;
        const unit = item.unit?.title || "";
        const quantityUsed = parseFloat(values[`quantity_${item.id}`]) || 0;
        
        if (values.consumption_status === "used_on_job" && quantityUsed > 0) {
          summary.used.push({
            product: productName,
            quantity: quantityUsed,
            unit: unit,
          });
        }
        
        const quantityNotUsed = quantityUsed === 0 ? item.quantity : (item.quantity - quantityUsed);
        if (quantityNotUsed > 0) {
          summary.needsHandling.push({
            product: productName,
            quantity: quantityNotUsed,
            unit: unit,
            reason: quantityUsed === 0 ? "Ikke brukt (0 registrert)" : "Ikke fullt ut brukt",
          });
        }
      });

      // Return summary data to parent
      onClose(summary);
      setSelectedItems([]);
    } catch (error) {
      console.error("Error updating items:", error);
      toast.error("Kunne ikke oppdatere produkter");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleItemSelection = (itemId, formik) => {
    const isSelected = selectedItems.includes(itemId);
    setSelectedItems((prev) =>
      isSelected
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
    
    // Auto-set max quantity if status is already selected and item is being selected
    if (!isSelected && formik && formik.values.consumption_status) {
      const item = pickedItems.find(i => i.id === itemId);
      if (item && (formik.values.consumption_status === "used_on_job" || formik.values.consumption_status === "taken_in_vehicle")) {
        const fieldName = `quantity_${itemId}`;
        const currentValue = formik.values[fieldName];
        if (!currentValue || currentValue === 0 || currentValue === null) {
          formik.setFieldValue(fieldName, item.quantity);
        }
      }
    }
  };

  const selectAll = () => {
    setSelectedItems(pickedItems.map((item) => item.id));
  };

  const deselectAll = () => {
    setSelectedItems([]);
  };

  return (
    <Transition.Root show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-[2100]" onClose={onClose}>
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                  Bulk-rediger vareforbruk
                </Dialog.Title>

                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {(formik) => (
                    <Form>
                      <div className="space-y-6">
                        {/* Select items */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Velg produkter å oppdatere ({selectedItems.length} valgt)
                            </FormLabel>
                            <div className="flex space-x-2">
                              <button
                                type="button"
                                onClick={selectAll}
                                className="text-sm text-blue-600 hover:text-blue-800"
                              >
                                Velg alle
                              </button>
                              <button
                                type="button"
                                onClick={deselectAll}
                                className="text-sm text-blue-600 hover:text-blue-800"
                              >
                                Fjern alle
                              </button>
                            </div>
                          </div>
                          <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                            {pickedItems.length === 0 ? (
                              <div className="p-4 text-center text-gray-500">
                                Ingen produkter er markert som plukket
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {pickedItems.map((item) => (
                                  <div
                                    key={item.id}
                                    className={`p-3 hover:bg-gray-50 ${formik.values.apply_to_all || selectedItems.includes(item.id) ? 'bg-blue-50' : ''}`}
                                  >
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex items-start flex-1 min-w-0">
                                        <Checkbox
                                          checked={selectedItems.includes(item.id) || formik.values.apply_to_all}
                                          onChange={() => toggleItemSelection(item.id, formik)}
                                          disabled={formik.values.apply_to_all}
                                          className="mt-1 flex-shrink-0"
                                        />
                                        <div className="ml-3 flex-1 min-w-0">
                                          <div className="text-sm font-medium text-gray-900">
                                            {item.product?.displayTitle || item.product?.title || item.title || `Produkt ${item.product_id}`}
                                          </div>
                                          {!formik.values.consumption_status && (
                                            <div className="text-sm text-gray-500">
                                              {item.quantity} {item.unit?.title || ""} plukket
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {/* Quantity input per item - only show when status is selected and item is selected */}
                                      {formik.values.consumption_status && (formik.values.apply_to_all || selectedItems.includes(item.id)) && (
                                        <div className="flex-shrink-0 w-24">
                                          <TextField
                                            name={`quantity_${item.id}`}
                                            type="number"
                                            size="small"
                                            variant="outlined"
                                            value={formik.values[`quantity_${item.id}`] || ""}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              formik.setFieldValue(`quantity_${item.id}`, value === "" ? "" : parseFloat(value) || 0);
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched[`quantity_${item.id}`] && Boolean(formik.errors[`quantity_${item.id}`])}
                                            helperText={formik.touched[`quantity_${item.id}`] && formik.errors[`quantity_${item.id}`] ? formik.errors[`quantity_${item.id}`] : `Maks ${item.quantity}`}
                                            inputProps={{
                                              step: "0.01",
                                              min: "0",
                                              max: item.quantity,
                                            }}
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                fontSize: "0.875rem",
                                              },
                                              "& .MuiInputBase-input": {
                                                padding: "8px 12px",
                                              },
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="mt-3">
                            <label className="flex items-center">
                              <Checkbox
                                checked={formik.values.apply_to_all}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  formik.setFieldValue("apply_to_all", isChecked);
                                  if (isChecked) {
                                    setSelectedItems([]);
                                    // Auto-set max quantity for all items if status is selected
                                    if (formik.values.consumption_status && 
                                        (formik.values.consumption_status === "used_on_job" || formik.values.consumption_status === "taken_in_vehicle")) {
                                      pickedItems.forEach((item) => {
                                        const fieldName = `quantity_${item.id}`;
                                        const currentValue = formik.values[fieldName];
                                        if (!currentValue || currentValue === 0 || currentValue === null) {
                                          formik.setFieldValue(fieldName, item.quantity);
                                        }
                                      });
                                    }
                                  }
                                }}
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                Gjelder for alle plukkede produkter ({pickedItems.length})
                              </span>
                            </label>
                          </div>
                        </div>

                        {/* Status selection */}
                        <div>
                          <FormLabel component="legend" className="text-sm font-medium text-gray-700 mb-3 block">
                            Status for vareforbruk
                          </FormLabel>
                          <RadioGroup
                            name="consumption_status"
                            value={formik.values.consumption_status}
                            onChange={(e) => {
                              const newStatus = e.target.value;
                              formik.setFieldValue("consumption_status", newStatus);
                              
                              // Auto-set max quantity for selected items when status is selected
                              if (newStatus === "used_on_job" || newStatus === "taken_in_vehicle") {
                                const itemsToUpdate = formik.values.apply_to_all 
                                  ? pickedItems 
                                  : pickedItems.filter(item => selectedItems.includes(item.id));
                                
                                itemsToUpdate.forEach((item) => {
                                  const fieldName = `quantity_${item.id}`;
                                  const currentValue = formik.values[fieldName];
                                  // Only set to max if current value is empty, 0, or null
                                  if (!currentValue || currentValue === 0 || currentValue === null) {
                                    formik.setFieldValue(fieldName, item.quantity);
                                  }
                                });
                              }
                            }}
                          >
                            <div className="space-y-3">
                              <div
                                className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                                  formik.values.consumption_status === "taken_in_vehicle"
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-300"
                                }`}
                                onClick={() => {
                                  formik.setFieldValue("consumption_status", "taken_in_vehicle");
                                  // Auto-set max quantity for selected items
                                  const itemsToUpdate = formik.values.apply_to_all 
                                    ? pickedItems 
                                    : pickedItems.filter(item => selectedItems.includes(item.id));
                                  
                                  itemsToUpdate.forEach((item) => {
                                    const fieldName = `quantity_${item.id}`;
                                    const currentValue = formik.values[fieldName];
                                    if (!currentValue || currentValue === 0 || currentValue === null) {
                                      formik.setFieldValue(fieldName, item.quantity);
                                    }
                                  });
                                }}
                              >
                                <FormControlLabel
                                  value="taken_in_vehicle"
                                  control={<Radio />}
                                  label={
                                    <div className="flex items-center space-x-3">
                                      <TruckIcon className="h-5 w-5 text-blue-600" />
                                      <div>
                                        <div className="font-medium text-gray-900">Tatt med i bilen</div>
                                        <div className="text-sm text-gray-500">Produktet er tatt med i bilen</div>
                                      </div>
                                    </div>
                                  }
                                />
                              </div>

                              <div
                                className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                                  formik.values.consumption_status === "used_on_job"
                                    ? "border-green-500 bg-green-50"
                                    : "border-gray-200 hover:border-green-300"
                                }`}
                                onClick={() => {
                                  formik.setFieldValue("consumption_status", "used_on_job");
                                  // Auto-set max quantity for selected items
                                  const itemsToUpdate = formik.values.apply_to_all 
                                    ? pickedItems 
                                    : pickedItems.filter(item => selectedItems.includes(item.id));
                                  
                                  itemsToUpdate.forEach((item) => {
                                    const fieldName = `quantity_${item.id}`;
                                    const currentValue = formik.values[fieldName];
                                    if (!currentValue || currentValue === 0 || currentValue === null) {
                                      formik.setFieldValue(fieldName, item.quantity);
                                    }
                                  });
                                }}
                              >
                                <FormControlLabel
                                  value="used_on_job"
                                  control={<Radio />}
                                  label={
                                    <div className="flex items-center space-x-3">
                                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                      <div>
                                        <div className="font-medium text-gray-900">Brukt på oppdraget</div>
                                        <div className="text-sm text-gray-500">Produktet er brukt på oppdraget</div>
                                      </div>
                                    </div>
                                  }
                                />
                              </div>
                            </div>
                          </RadioGroup>
                        </div>


                        <div className="flex justify-end space-x-3 pt-4 border-t">
                          <PMButton
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            disabled={formik.isSubmitting}
                          >
                            Avbryt
                          </PMButton>
                          <PMButton
                            type="submit"
                            disabled={formik.isSubmitting || (!formik.values.apply_to_all && selectedItems.length === 0)}
                            loading={formik.isSubmitting}
                          >
                            Oppdater ({formik.values.apply_to_all ? pickedItems.length : selectedItems.length} produkter)
                          </PMButton>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

